// Capacity-based turnkey plant landing page data (20–200 BPM)
const IMG = 'https://static.prod-images.emergentagent.com/jobs/932fe87d-11cd-44ee-9b8b-ca40c329d944/images';
export const CAPACITY_HERO = `${IMG}/fee152e3e8af8f2f2db9442eb1155451bdd1947137f5ca671d324b946f16f322.jpeg`;

const CLIENT_SCOPE = [
  'Land and covered building/shed as per layout',
  'Civil work — flooring with slopes, drainage, partitioning',
  'Borewell / raw water source with storage tank',
  'Three-phase power connection at specified load',
  'Statutory license fees (BIS, FSSAI, pollution board)',
  'Plant manpower and packaging raw material',
  'Unloading arrangement at site (crane/forklift where needed)'
];

const ALDEN_SCOPE = [
  'Plant layout design matched to your building & BIS norms',
  'Complete machinery manufacturing and factory testing',
  'RO treatment system engineered to your water report',
  'Delivery, erection, piping and electrical integration',
  'Commissioning, line balancing and trial production',
  'Operator training with documented SOPs',
  'BIS / FSSAI technical documentation support',
  'After-sales service, spares and AMC support PAN India'
];

const COMMON_FAQ = (bpm, invest, payback) => ([
  {
    q: `What is the total cost to set up a ${bpm} BPM mineral water plant?`,
    a: `Complete machinery investment is approximately ${invest} for the ${bpm} BPM turnkey configuration. Additionally budget ₹3–5 Lakhs for licensing (BIS, FSSAI, pollution consents) and 2–3 months of working capital. Land and building are in client scope.`
  },
  {
    q: `How long does a ${bpm} BPM plant take from order to production?`,
    a: 'Machinery manufacturing takes 45–60 days, installation and commissioning 2–4 weeks, with BIS/FSSAI licensing processed in parallel over 60–90 days. Site readiness on time is the key factor.'
  },
  {
    q: `What payback period can I realistically expect?`,
    a: `Most well-distributed ${bpm} BPM plants recover machinery investment in ${payback}. Actual payback depends on capacity utilisation, distribution strength and packaging material buying — we share a detailed ROI worksheet with every quotation, without unrealistic guarantees.`
  }
]);

export const capacityPlants = {
  '/20-bpm-water-plant': {
    bpm: '20 BPM', bph: '1,200 BPH', daily: '~85,000 bottles/day (2 shifts)', investment: '₹12 – 18 Lakhs',
    space: '2,000 – 2,500 sq. ft.', power: '20 – 25 HP', manpower: '5 – 6 workers/shift', timeline: '60 – 75 days',
    h1: '20 BPM Mineral Water Plant',
    sub: 'Entry-level turnkey plant for first-time entrepreneurs and small-town distribution',
    metaTitle: '20 BPM Mineral Water Plant | Cost, Machinery & Complete Setup | Alden Technologies',
    metaDescription: 'Set up a 20 BPM mineral water plant (1,200 bottles/hour) in ₹12–18 Lakhs. Complete configuration: machinery, space, power, manpower, timeline and Alden vs client scope. Free quotation.',
    machinery: [
      '1000–2000 LPH RO Plant with UV & Ozonation', '20 BPM Automatic Rinsing-Filling-Capping Machine',
      'Semi-Automatic PET Bottle Blowing Machine (1-cavity)', 'Sticker Labelling Machine (Semi-Auto)',
      'Web Sealer / Shrink Tunnel', 'Batch Coding Machine', 'SS Storage Tanks & Interconnecting Piping',
      'BIS-Compliant Lab Equipment Package'
    ],
    optional: ['20-Litre Jar Rinsing-Filling-Capping Line', 'Automatic Labelling Upgrade', 'Bottle Unscrambler', 'Water Softener (for hard water sources)'],
    faqs: COMMON_FAQ('20', '₹12 – 18 Lakhs', '24 – 30 months')
  },
  '/24-bpm-water-plant': {
    bpm: '24 BPM', bph: '1,440 BPH', daily: '~1 Lakh bottles/day (2 shifts)', investment: '₹15 – 22 Lakhs',
    space: '2,500 – 3,000 sq. ft.', power: '25 – 30 HP', manpower: '5 – 7 workers/shift', timeline: '60 – 75 days',
    h1: '24 BPM Mineral Water Plant',
    sub: 'The most popular starter configuration — district-level distribution at the lowest viable investment',
    metaTitle: '24 BPM Mineral Water Plant | ₹15–22 Lakh Turnkey Setup | Alden Technologies',
    metaDescription: 'Complete 24 BPM mineral water bottling plant (1,440 bottles/hour) in ₹15–22 Lakhs — machinery list, plant configuration, space, power, manpower, timeline and scope. Get a free project report.',
    machinery: [
      '2000 LPH RO Plant with UV & Ozonation', '24 BPM Automatic Rinsing-Filling-Capping Machine',
      'Semi-Automatic PET Bottle Blowing Machine (2-cavity)', 'Sticker Labelling Machine',
      'Web Sealer / Shrink Wrapping Machine', 'Batch Coding Machine', 'SS Storage Tanks & Piping',
      'High-Pressure Air Compressor & Chiller', 'BIS-Compliant Lab Equipment Package'
    ],
    optional: ['20-Litre Jar Line', 'Automatic Rotary Labelling Upgrade', 'Bottle Unscrambler', 'Air Conveyor from Blower to Filler'],
    faqs: COMMON_FAQ('24', '₹15 – 22 Lakhs', '24 – 30 months')
  },
  '/30-bpm-water-plant': {
    bpm: '30 BPM', bph: '1,800 BPH', daily: '~1.25 Lakh bottles/day (2 shifts)', investment: '₹20 – 28 Lakhs',
    space: '2,500 – 3,500 sq. ft.', power: '30 – 35 HP', manpower: '6 – 7 workers/shift', timeline: '60 – 90 days',
    h1: '30 BPM Mineral Water Plant',
    sub: 'Growing-brand configuration with headroom for multi-town distribution',
    metaTitle: '30 BPM Mineral Water Plant | Cost & Complete Machinery List | Alden Technologies',
    metaDescription: 'Set up a 30 BPM water bottling plant (1,800 bottles/hour) in ₹20–28 Lakhs. Full plant configuration, machinery included, space, power and manpower requirements, Alden vs client scope.',
    machinery: [
      '2000–3000 LPH RO Plant with UV & Ozonation', '30 BPM Automatic Rinsing-Filling-Capping Machine',
      'Semi-Automatic PET Bottle Blowing Machine (2-cavity)', 'Sticker Labelling Machine',
      'Shrink Wrapping Machine with Tunnel', 'Batch Coding Machine', 'SS Storage Tanks & Piping',
      'High-Pressure Air Compressor & Chiller', 'BIS-Compliant Lab Equipment Package'
    ],
    optional: ['20-Litre Jar Line', 'Automatic Blowing Upgrade', 'Bottle Unscrambler', 'Air & Slat Conveyor Package'],
    faqs: COMMON_FAQ('30', '₹20 – 28 Lakhs', '24 – 30 months')
  },
  '/40-bpm-water-plant': {
    bpm: '40 BPM', bph: '2,400 BPH', daily: '~1.7 Lakh bottles/day (2 shifts)', investment: '₹28 – 38 Lakhs',
    space: '3,000 – 4,000 sq. ft.', power: '35 – 40 HP', manpower: '6 – 8 workers/shift', timeline: '60 – 90 days',
    h1: '40 BPM Mineral Water Plant',
    sub: 'Semi-commercial rotary configuration for full district distribution',
    metaTitle: '40 BPM Mineral Water Plant | Semi-Commercial Turnkey Setup | Alden Technologies',
    metaDescription: '40 BPM mineral water plant (2,400 bottles/hour) turnkey setup in ₹28–38 Lakhs — rotary RFC machine, 3000 LPH RO, blowing, packaging, complete configuration and scope details.',
    machinery: [
      '3000 LPH RO Plant with UV & Ozonation', '40 BPM Rotary Rinsing-Filling-Capping Monoblock',
      'Semi-Automatic PET Bottle Blowing Machine (2-cavity)', 'Automatic Sticker Labelling Machine',
      'Shrink Wrapping Machine with Tunnel', 'Batch Coding Machine', 'Slat Conveyors & SS Tanks',
      'High-Pressure Air Compressor, Dryer & Chiller', 'BIS-Compliant Lab Equipment Package'
    ],
    optional: ['20-Litre Jar Line', 'Fully Automatic Blowing Machine', 'Bottle Unscrambler', 'Air Conveyor Package', 'Cartoning Machine'],
    faqs: COMMON_FAQ('40', '₹28 – 38 Lakhs', '24 – 30 months')
  },
  '/60-bpm-water-plant': {
    bpm: '60 BPM', bph: '3,600 BPH', daily: '~2.5 Lakh bottles/day (2 shifts)', investment: '₹45 – 60 Lakhs',
    space: '5,000 – 6,000 sq. ft.', power: '50 – 60 HP', manpower: '8 – 10 workers/shift', timeline: '75 – 100 days',
    h1: '60 BPM Mineral Water Plant',
    sub: 'Commercial city-level plant with full line automation',
    metaTitle: '60 BPM Mineral Water Plant | Commercial Scale Bottling Setup | Alden Technologies',
    metaDescription: 'Commercial 60 BPM water bottling plant (3,600 bottles/hour) in ₹45–60 Lakhs — 5000 LPH RO, rotary filling, automatic blowing option, complete configuration, scope and timeline.',
    machinery: [
      '5000 LPH RO Plant with UV & Ozonation', '60 BPM Rotary Rinsing-Filling-Capping Monoblock',
      'Automatic PET Bottle Blowing Machine (4-cavity) or 2x Semi-Auto', 'Automatic Rotary Sticker Labelling Machine',
      'Automatic Shrink Wrapping Machine', 'Batch Coding Machine', 'Air Conveyor + Slat Conveyor Package',
      'Bottle Unscrambler (recommended)', 'High-Pressure Compressors, Dryer & Chiller', 'BIS-Compliant Lab Equipment Package'
    ],
    optional: ['20-Litre Jar Line', 'Cartoning / Case Packing Machine', 'Accumulation Table', 'Preform & Cap Elevators'],
    faqs: COMMON_FAQ('60', '₹45 – 60 Lakhs', '26 – 32 months')
  },
  '/90-bpm-water-plant': {
    bpm: '90 BPM', bph: '5,400 BPH', daily: '~3.8 Lakh bottles/day (2 shifts)', investment: '₹75 – 95 Lakhs',
    space: '7,000 – 9,000 sq. ft.', power: '70 – 85 HP', manpower: '10 – 12 workers/shift', timeline: '90 – 110 days',
    h1: '90 BPM Mineral Water Plant',
    sub: 'High-capacity regional plant for multi-district brands',
    metaTitle: '90 BPM Mineral Water Plant | High-Capacity Industrial Setup | Alden Technologies',
    metaDescription: '90 BPM industrial water bottling plant (5,400 bottles/hour) in ₹75–95 Lakhs — high-speed rotary filling, automatic blowing, full automation. Configuration, machinery and scope details.',
    machinery: [
      '6000–8000 LPH RO Plant with UV & Ozonation', '90 BPM Rotary Rinsing-Filling-Capping Monoblock',
      'Fully Automatic PET Bottle Blowing Machine (4-cavity)', 'Automatic Rotary Labelling Machine',
      'Automatic Shrink Wrapping Machine', 'Inkjet Batch Coding Machine', 'Air Conveyor + Slat Conveyor Package',
      'Bottle Unscrambler', 'High-Pressure Compressors, Dryers & Chillers', 'BIS-Compliant Lab Equipment Package'
    ],
    optional: ['20-Litre Jar Line', 'Cartoning Machine', 'Second Blowing Machine for Redundancy', 'SCADA Line Monitoring'],
    faqs: COMMON_FAQ('90', '₹75 – 95 Lakhs', '26 – 32 months')
  },
  '/120-bpm-water-plant': {
    bpm: '120 BPM', bph: '7,200 BPH', daily: '~5 Lakh bottles/day (2 shifts)', investment: '₹1.4 – 1.6 Crore',
    space: '10,000 – 12,000 sq. ft.', power: '90 – 110 HP', manpower: '12 – 15 workers/shift', timeline: '90 – 120 days',
    h1: '120 BPM Mineral Water Plant',
    sub: 'Enterprise-scale plant for state-level distribution',
    metaTitle: '120 BPM Mineral Water Plant | Enterprise Bottling Solution | Alden Technologies',
    metaDescription: '120 BPM enterprise water bottling plant (7,200 bottles/hour) in ₹1.4–1.6 Crore — 10000 LPH RO, servo rotary filling, full automation. Complete configuration, machinery and scope.',
    machinery: [
      '10000 LPH RO Plant with UV & Ozonation', '120 BPM Servo Rotary Rinsing-Filling-Capping Monoblock',
      'Fully Automatic PET Bottle Blowing Machine (6-cavity)', 'Automatic Rotary Labelling Machine',
      'Automatic Shrink Wrapping Machine', 'High-Speed Inkjet Coding', 'Complete Air + Slat Conveyor Package',
      'Bottle Unscrambler', 'High-Pressure Compressor Bank, Dryers & Chillers', 'PLC Line Control + BIS Lab Package'
    ],
    optional: ['20-Litre Jar Line', 'Cartoning / Case Packing Line', 'SCADA with Production MIS', 'Palletizing Section'],
    faqs: COMMON_FAQ('120', '₹1.4 – 1.6 Crore', '30 – 36 months')
  },
  '/200-bpm-water-plant': {
    bpm: '200 BPM', bph: '12,000 BPH', daily: '~8.5 Lakh bottles/day (2 shifts)', investment: '₹2.5 – 3.2 Crore',
    space: '15,000 – 20,000 sq. ft.', power: '150 – 180 HP', manpower: '18 – 25 workers/shift', timeline: '110 – 140 days',
    h1: '200 BPM Mineral Water Plant',
    sub: 'Flagship enterprise line for national brands and co-packing contracts',
    metaTitle: '200 BPM Mineral Water Bottling Plant | Cost, Machinery & Setup | Alden Technologies',
    metaDescription: '200 BPM mineral water plant producing 12,000 bottles/hour — ₹2.5–3.2 Crore turnkey investment with dual RO trains, 40-head servo filling and full SCADA automation. Complete configuration.',
    machinery: [
      '10000 LPH RO Plant (dual train) with Ozonation & UV', '200 BPM Rotary RFC Monoblock (40 filling heads, servo)',
      '2x Fully Automatic PET Blowing Machines (4-cavity each)', 'Automatic Rotary Labelling Machine (high speed)',
      'Web Sealer + Automatic Shrink Wrapping', 'High-Speed Inkjet Coding', 'Complete Air + Slat + Packing Conveyors',
      'Bottle Unscrambler', 'High-Pressure Compressor Bank, Dryers & Chillers', 'PLC + SCADA with Production MIS', 'BIS Lab Package'
    ],
    optional: ['20-Litre Jar Line', 'Automatic Case Packing & Palletizing', 'Preform Injection Moulding Section', 'Co-packing Changeover Kits'],
    faqs: COMMON_FAQ('200', '₹2.5 – 3.2 Crore', '24 – 30 months')
  }
};
